import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { CalendarDays } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "@/components/ui/input-group";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { Estimation } from "@/interfaces/estimation.interface";

/** Below this, a year is a half-typed or corrupted entry rather than a real date. */
const MIN_YEAR = 1900;

const ISO_DATE = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
const US_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{1,4})$/;

interface DateParts {
  year: number;
  month: number;
  day: number;
}

/** Stored and typed dates come in two shapes: ISO `YYYY-MM-DD` and `M/D/YYYY`. */
const toParts = (value: string): DateParts | null => {
  const trimmed = value.trim();

  const iso = ISO_DATE.exec(trimmed);
  if (iso) return { year: Number(iso[1]), month: Number(iso[2]), day: Number(iso[3]) };

  const us = US_DATE.exec(trimmed);
  if (us) return { year: Number(us[3]), month: Number(us[1]), day: Number(us[2]) };

  return null;
};

/**
 * Built from parts rather than `new Date(string)`, whose ISO branch parses as UTC and shifts the day
 * west of Greenwich. Null when the parts do not name a real day, e.g. 2/30 or 2/29 of a common year.
 */
const toDate = ({ year, month, day }: DateParts): Date | null => {
  const date = new Date(year, month - 1, day);
  const isReal = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  return isReal ? date : null;
};

const parseStoredDate = (value: string | undefined): Date | undefined => {
  if (!value) return undefined;
  const parts = toParts(value);
  if (!parts || parts.year < MIN_YEAR) return undefined;
  return toDate(parts) ?? undefined;
};

const endOfToday = () => {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  return end;
};

/** Why typed text is not a usable date, or null when it is fine. */
const getTypedError = (text: string): string | null => {
  const parts = toParts(text);
  if (!parts) return "Use M/D/YYYY.";
  if (parts.year < MIN_YEAR) return `Enter a year after ${MIN_YEAR}.`;

  const date = toDate(parts);
  if (!date) return "That date does not exist.";
  if (date > endOfToday()) return "This date cannot be in the future.";

  return null;
};

interface DateFieldProps {
  label: string;
  name: keyof Estimation;
  value?: string;
  placeholder?: string;
  estimation: Estimation | undefined;
  updateEstimationDetails: (_estimation: Estimation, field: keyof Estimation, clientOnly?: boolean) => Promise<boolean>;
}

export const DateField = ({ label, name, value, placeholder, estimation, updateEstimationDetails }: DateFieldProps) => {
  const [text, setText] = useState(() => {
    const parsed = parseStoredDate(value);
    return parsed ? parsed.toLocaleDateString("en-US") : (value ?? "");
  });
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const errorId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  /** Escape blurs before React re-renders, so the blur handler would otherwise commit the reverted text. */
  const escapeCancelledRef = useRef(false);

  const selected = useMemo(() => parseStoredDate(value), [value]);
  const today = useMemo(() => new Date(), []);
  // Controlled, so the caption dropdowns drive navigation instead of re-rendering an uncontrolled month.
  const [month, setMonth] = useState<Date | undefined>(selected);

  // Keep the input text in sync when the stored value changes from outside.
  useEffect(() => {
    const display = selected ? selected.toLocaleDateString("en-US") : (value ?? "");
    setText(display);
    setMonth(selected);
  }, [value, selected]);

  const commit = useCallback(
    (date: Date) => {
      if (!estimation) return;

      // Kept as M/D/YYYY: what the field has always written, and what the Verisk export reads back.
      const next = date.toLocaleDateString("en-US");
      if (next === value) return;

      const newEstimation = structuredClone(estimation);
      newEstimation[name] = next as never;
      updateEstimationDetails(newEstimation, name);
    },
    [estimation, name, updateEstimationDetails, value],
  );

  // Typed text commits on blur and Enter, never per keystroke, so a partial entry is never written.
  const commitTyped = useCallback(
    (raw: string) => {
      if (!raw.trim().length) {
        // Restore the last committed value on an empty blur.
        const display = selected ? selected.toLocaleDateString("en-US") : (value ?? "");
        setText(display);
        setError(null);
        return;
      }

      const typedError = getTypedError(raw);
      if (typedError) {
        setError(typedError);
        return;
      }

      commit(toDate(toParts(raw)!)!);
      setError(null);
    },
    [commit, selected, value],
  );

  // On the input, not an ancestor: React routes portal events through the tree, so a handler above the
  // popover would also see focus moving inside the calendar. Closing it refocuses the input, so
  // dismissing without picking still ends in a blur here.
  const onInputBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (escapeCancelledRef.current) {
        escapeCancelledRef.current = false;
        return;
      }
      if (open) return;
      const movedTo = e.relatedTarget as Node | null;
      if (movedTo && containerRef.current?.contains(movedTo)) return;

      commitTyped(e.target.value);
    },
    [commitTyped, open],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        commitTyped(e.currentTarget.value);
      } else if (e.key === "Escape") {
        e.preventDefault();
        // Restore the last committed value without committing the edit.
        const display = selected ? selected.toLocaleDateString("en-US") : (value ?? "");
        setText(display);
        setError(null);
        escapeCancelledRef.current = true;
        inputRef.current?.blur();
      }
    },
    [commitTyped, selected, value],
  );

  const onSelectDate = useCallback(
    (date: Date) => {
      commit(date);
      setError(null);
      setOpen(false);
    },
    [commit],
  );

  return (
    <div className="grid grid-cols-3">
      <div className="flex w-[100px] flex-col justify-center text-xs font-medium whitespace-nowrap text-[#71717A]">
        <label>{label}</label>
      </div>
      <div className="col-span-2">
        <div ref={containerRef}>
          {/* The calendar sits outside InputGroupAddon on purpose: React routes portal events up the
              component tree, and the addon focuses the input on any click that is not on a button —
              which is every click in the calendar, and pulling focus out closes the popover. */}
          <Popover open={open} onOpenChange={setOpen}>
            <InputGroup className="h-7 max-w-[200px] rounded-lg">
              <InputGroupAddon align="inline-start">
                <PopoverTrigger asChild>
                  <InputGroupButton type="button" aria-label={`Pick ${label.toLowerCase()} from a calendar`}>
                    <CalendarDays className="size-4" />
                  </InputGroupButton>
                </PopoverTrigger>
              </InputGroupAddon>
              <InputGroupInput
                ref={inputRef}
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  setError(null);
                }}
                onBlur={onInputBlur}
                onKeyDown={onKeyDown}
                placeholder={placeholder ?? "M/D/YYYY"}
                aria-label={label}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId : undefined}
                className="text-sm"
              />
            </InputGroup>
            <PopoverContent
              className="w-auto p-0"
              align="start"
              // Hand focus back to the input, so dismissing without picking still commits what was typed.
              onCloseAutoFocus={(e) => {
                e.preventDefault();
                inputRef.current?.focus();
              }}
            >
              <Calendar
                mode="single"
                // Clearing was never supported; without this, re-clicking the selected day silently does nothing.
                required
                selected={selected}
                month={month}
                onMonthChange={setMonth}
                onSelect={onSelectDate}
                disabled={{ after: today }}
                captionLayout="dropdown"
                startMonth={new Date(MIN_YEAR, 0)}
                endMonth={today}
              />
            </PopoverContent>
          </Popover>
        </div>
        {error ? (
          <p id={errorId} role="alert" className="text-destructive mt-1 text-xs leading-snug">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
};
